// NOTE: "use client" is a Next.js directive; safe to keep here (ignored by Vite/React)
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User,
  Phone,
  BookOpen,
  Layers,
  FileText,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

// Option constants
const courseOptions = [
  "COPA (Computer Operator & Programming Assistant)",
  "Draughtsman (Civil)",
  "Secretarial Assistant",
  "Fitter",
  "Welder",
  "Plumber",
  "Carpenter",
  "Medical Lab Technician (MLT)",
  "Phlebotomy Technician",
  "Lab Assistant / Pathology Assistant",
  "Dialysis Technician",
  "ICU Technician",
  "ECG Technician",
];
const bloodGroups = [
  "",
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
];
const states = [
  "",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Jammu & Kashmir",
  "Puducherry",
];

// Steps visual config
const steps = [
  {
    title: "Personal & Contact Info",
    description: "Fill in your personal details.",
    icon: User,
    active: true,
  },
  {
    title: "Academic & Course",
    description: "Select academics & your course.",
    icon: BookOpen,
    active: true,
  },
  {
    title: "Upload & Declaration",
    description: "Upload files, sign and submit.",
    icon: FileText,
    active: true,
  },
];

// Add this helper at the top of your component (before AdmissionsSection)
const safeJson = async (resp) => {
  try {
    const contentType = resp.headers?.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return await resp.json();
    }
    const text = await resp.text();
    if (/^\s*</.test(text)) {
      throw new Error("A server error occurred (non-JSON response)");
    }
    throw new Error(text || "Unknown server error");
  } catch (err) {
    return {
      error: true,
      message: err?.message || "Failed to parse server response",
    };
  }
};

const MAX_SIZE = 5 * 1024 * 1024;
const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

const AdmissionsSection = () => {
  const [form, setForm] = useState({
    full_name: "",
    date_of_birth: "",
    gender: "",
    aadhar_card_number: "",
    father_name: "",
    mother_name: "",
    blood_group: "",
    mobile_number: "",
    email_address: "",
    permanent_address: "",
    city_district: "",
    state: "",
    pin_code: "",
    highest_qualification: "",
    board_university: "",
    year_of_passing: "",
    percentage_cgpa: "",
    school_college_name: "",
    preferred_course: "",
    batch_time_preference: "",
    passport_size_photo: null,
    marksheet_10_12: null,
    aadhar_card_copy: null,
    signature: null,
    caste_certificate: null,
    declaration: false,
    declaration_date: "",
    declaration_place: "",
    applicant_signature: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Handle text, select, radio, checkbox
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle file inputs with validation
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (file) {
      if (file.size > MAX_SIZE) {
        setErrors((prev) => ({ ...prev, [name]: "File must be ≤ 5MB." }));
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: "File must be PDF/JPG/PNG.",
        }));
        return;
      }
      setForm((prev) => ({ ...prev, [name]: file }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate all required and formatted inputs
  const validateForm = (data) => {
    const n = {};
    if (!data.full_name.trim()) n.full_name = "Full name is required.";
    if (!data.date_of_birth) n.date_of_birth = "DOB is required.";
    if (!data.gender) n.gender = "Gender is required.";
    if (!data.aadhar_card_number) n.aadhar_card_number = "Aadhar card no. required.";
    else if (!/^\d{12}$/.test(data.aadhar_card_number))
      n.aadhar_card_number = "12 digit Aadhar required.";
    if (!data.father_name.trim()) n.father_name = "Father's name required.";
    if (!data.mother_name.trim()) n.mother_name = "Mother's name required.";
    if (!data.mobile_number) n.mobile_number = "Mobile no. required.";
    else if (!/^[6-9]\d{9}$/.test(data.mobile_number))
      n.mobile_number = "Must be 10 digits starting 6-9.";
    if (!data.email_address) n.email_address = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email_address))
      n.email_address = "Invalid email format.";
    if (!data.permanent_address.trim())
      n.permanent_address = "Permanent address required.";
    if (!data.city_district.trim()) n.city_district = "City/District required.";
    if (!data.state) n.state = "State required.";
    if (!data.pin_code) n.pin_code = "PIN code required.";
    else if (!/^[1-9][0-9]{5}$/.test(data.pin_code))
      n.pin_code = "6 digits, not starting with 0.";
    if (!data.highest_qualification)
      n.highest_qualification = "Select highest qualification.";
    if (!data.preferred_course) n.preferred_course = "Select preferred course.";
    if (!data.batch_time_preference)
      n.batch_time_preference = "Select batch time.";
    if (!data.declaration) n.declaration = "Please accept declaration.";
    if (!data.declaration_date) n.declaration_date = "Declaration date required.";
    if (!data.declaration_place.trim()) n.declaration_place = "Place is required.";
    // File validation example (optional to enforce file is present)
    // if (!data.passport_size_photo) n.passport_size_photo = "Photo required.";
    return n;
  };

  // Add this uploadFile function inside your component
  const uploadFile = async (file, folder) => {
    if (!file) return null;
    try {
      const filename = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });

      const response = await fetch('/api/upload-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: base64, folder, filename }),
      });

      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.details || errorData.error || 'Upload failed');
      }
      const data = await response.json();
      return data.path;
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        submit: err.message || "File upload failed. Please try again or contact support."
      }));
      return null;
    }
  };

  // Replace the entire handleSubmit function with this:
  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- VALIDATION ---
    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(prev => ({
        ...prev,
        ...validationErrors,
        submit: "Please fill all required fields correctly.",
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (loading || isSubmitting) return;

    setLoading(true);
    setIsSubmitting(true);
    setErrors({});

    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';

    try {
      // --- UPLOAD FILES ---
      const uploadResults = await Promise.all([
        uploadFile(form.passport_size_photo, 'passportPhotos'),
        uploadFile(form.marksheet_10_12, 'marksheets'),
        uploadFile(form.aadhar_card_copy, 'aadharCards'),
        uploadFile(form.signature, 'signatures'),
        uploadFile(form.caste_certificate, 'casteCertificates'),
        uploadFile(form.applicant_signature, 'applicantSignatures'),
      ]);

      const [
        passportPhotoPath,
        marksheetPath,
        aadharPath,
        signaturePath,
        casteCertPath,
        applicantSigPath
      ] = uploadResults;

      // --- FILE UPLOAD VALIDATION ---
      const requiredUploads = [
        { file: form.passport_size_photo, path: passportPhotoPath, name: 'Passport photo' },
        { file: form.marksheet_10_12, path: marksheetPath, name: 'Marksheet' },
        { file: form.aadhar_card_copy, path: aadharPath, name: 'Aadhar card' },
        { file: form.signature, path: signaturePath, name: 'Signature' },
      ];

      for (const { file, path, name } of requiredUploads) {
        if (file && !path) throw new Error(`${name} upload failed`);
      }

      // --- BUILD PAYLOAD ---
      // Use schema/API field names for uploaded file paths
      const admissionData = {
        ...form,
        passport_size_photo: passportPhotoPath,
        marksheet_10_12: marksheetPath,
        aadhar_card_copy: aadharPath,
        signature: signaturePath,
        caste_certificate: casteCertPath,
        applicant_signature: applicantSigPath,
      };

      // --- SUBMIT API ---
      const response = await fetch('/api/student-admissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(admissionData),
      });

      let result;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const textResponse = await response.text();
        console.error('Non-JSON response received:', {
          status: response.status,
          statusText: response.statusText,
          contentType,
          responsePreview: textResponse.substring(0, 200)
        });
        throw new Error('Server or network error occurred. Please try again later or contact us at info@meenamma-trust.org');
      }

      if (result.success) {
        alert(
          `✅ Admission Application Submitted Successfully!\n\n` +
          `Confirmation emails have been sent to:\n` +
          `• Your email: ${form.email_address}\n` +
          `• Our office: info@meenamma-trust.org\n\n` +
          `We will contact you within 2-4 weeks.\n\n` +
          `Thank you for applying!`
        );
        setSubmitSuccess(true);

        // Reset form
        setForm({
          full_name: "", date_of_birth: "", gender: "", aadhar_card_number: "",
          father_name: "", mother_name: "", blood_group: "", mobile_number: "",
          email_address: "", permanent_address: "", city_district: "", state: "",
          pin_code: "", highest_qualification: "", board_university: "",
          year_of_passing: "", percentage_cgpa: "", school_college_name: "",
          preferred_course: "", batch_time_preference: "",
          passport_size_photo: null, marksheet_10_12: null,
          aadhar_card_copy: null, signature: null,
          caste_certificate: null, declaration: false,
          declaration_date: "", declaration_place: "",
          applicant_signature: null,
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => { setSubmitSuccess(false); }, 5000);
      } else {
        throw new Error(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submission error:', error);

      const detailedError = error.message || "Application submission failed";
      setErrors(prev => ({
        ...prev,
        submit: detailedError
      }));

      alert(
        `❌ Submission Failed\n\n` +
        `Error: ${detailedError}\n\n` +
        `Please try again or contact us at info@meenamma-trust.org`
      );
    } finally {
      setIsSubmitting(false);
      setLoading(false);
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  };

  return (
    <section id="admissions" className="py-20 md:py-28 px-4 mesh-gradient">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
            Admission Form
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Apply for admission to vocational programs. Please fill all details and upload documents clearly and accurately.
          </p>
        </motion.div>

        {/* Steps Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10"
        >
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={idx}
                className={`relative p-6 rounded-xl ${step.active
                  ? "bg-gradient-to-br from-brand-indigo to-brand-emerald text-white shadow-xl"
                  : "bg-white text-muted-foreground shadow-md"
                  }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`p-3 rounded-lg ${step.active
                      ? "bg-white/20"
                      : "bg-brand-indigo/15"
                      }`}
                  >
                    <Icon
                      className={step.active ? "text-white" : "text-blue-600"}
                      size={24}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{step.title}</h3>
                    <p
                      className={`text-sm ${step.active ? "text-white/90" : "text-muted-foreground"
                        }`}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-0.5 bg-gray-300" />
                )}
              </div>
            );
          })}
        </motion.div>

        {/* Warning box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 p-6 bg-brand-amber/10 border-2 border-brand-amber/30 rounded-xl"
        >
          <div className="flex items-start gap-4">
            <AlertCircle className="text-brand-amber flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-bold text-foreground text-lg mb-2">
                Required Documents
              </h3>
              <p className="text-muted-foreground mb-3">
                After filling this form, please email the following documents to{" "}
                <span className="font-semibold">info@meenamma-trust.org</span>:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Income Certificate</li>
                <li>Academic Records (Previous year mark sheets)</li>
                <li>ID Proof (Aadhaar Card copy)</li>
                <li>Bank Passbook (First page copy)</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Main Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="glassmorphism-card border border-border/60 rounded-2xl shadow-xl p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-12 text-base">
            {/* Personal Details */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text flex items-center gap-2">
                <User size={28} className="text-blue-600" />
                Personal Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Full Name (As per Aadhar Card)</label>
                  <Input name="full_name" value={form.full_name} onChange={handleInputChange} required className={errors.full_name ? "border-red-500" : ""} />
                  {errors.full_name && <span className="text-red-500 text-sm">{errors.full_name}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Date of Birth</label>
                  <Input type="date" name="date_of_birth" value={form.date_of_birth} onChange={handleInputChange} required className={errors.date_of_birth ? "border-red-500" : ""} />
                  {errors.date_of_birth && <span className="text-red-500 text-sm">{errors.date_of_birth}</span>}</div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Gender</label>
                  <select name="gender" value={form.gender} onChange={handleInputChange} required className={`field-base ${errors.gender ? "field-error" : ""}`}>
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                  {errors.gender && <span className="text-red-500 text-sm">{errors.gender}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Aadhar Card Number</label>
                  <Input name="aadhar_card_number" maxLength={12} value={form.aadhar_card_number} onChange={handleInputChange} required className={errors.aadhar_card_number ? "border-red-500" : ""} />
                  {errors.aadhar_card_number && <span className="text-red-500 text-sm">{errors.aadhar_card_number}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Father’s Name</label>
                  <Input name="father_name" value={form.father_name} onChange={handleInputChange} required className={errors.father_name ? "border-red-500" : ""} />
                  {errors.father_name && <span className="text-red-500 text-sm">{errors.father_name}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Mother’s Name</label>
                  <Input name="mother_name" value={form.mother_name} onChange={handleInputChange} required className={errors.mother_name ? "border-red-500" : ""} />
                  {errors.mother_name && <span className="text-red-500 text-sm">{errors.mother_name}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Blood Group</label>
                  <select className="field-base" name="blood_group" value={form.blood_group} onChange={handleInputChange}>
                    {bloodGroups.map((bg) => <option key={bg} value={bg}>{bg}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text flex items-center gap-2">
                <Phone size={28} className="text-sky-600" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Mobile Number (WhatsApp)</label>
                  <Input name="mobile_number" maxLength={10} value={form.mobile_number} onChange={handleInputChange} required className={errors.mobile_number ? "border-red-500" : ""} />
                  {errors.mobile_number && <span className="text-red-500 text-sm">{errors.mobile_number}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Email Address</label>
                  <Input type="email" name="email_address" value={form.email_address} onChange={handleInputChange} required className={errors.email_address ? "border-red-500" : ""} />
                  {errors.email_address && <span className="text-red-500 text-sm">{errors.email_address}</span>}
                </div>
                <div className="md:col-span-2"><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Permanent Address</label>
                  <textarea name="permanent_address" value={form.permanent_address} onChange={handleInputChange} rows="3" className={`field-base ${errors.permanent_address ? "field-error" : ""}`} />
                  {errors.permanent_address && <span className="text-red-500 text-sm">{errors.permanent_address}</span>}</div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">City/District</label>
                  <Input name="city_district" value={form.city_district} onChange={handleInputChange} required className={errors.city_district ? "border-red-500" : ""} />
                  {errors.city_district && <span className="text-red-500 text-sm">{errors.city_district}</span>}</div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">State</label>
                  <select name="state" value={form.state} onChange={handleInputChange} required className={`field-base ${errors.state ? "field-error" : ""}`}>
                    {states.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <span className="text-red-500 text-sm">{errors.state}</span>}
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">PIN Code</label>
                  <Input name="pin_code" maxLength={6} value={form.pin_code} onChange={handleInputChange} required className={errors.pin_code ? "border-red-500" : ""} />
                  {errors.pin_code && <span className="text-red-500 text-sm">{errors.pin_code}</span>}
                </div>
              </div>
            </div>

            {/* Academic Qualification & Course */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text flex items-center gap-2">
                <BookOpen size={28} className="text-lime-600" />
                Academic Qualification &amp; Course Selection
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Highest Qualification</label>
                  <div className="flex flex-wrap gap-4">
                    <label><input type="radio" name="highest_qualification" value="10th Pass"
                      checked={form.highest_qualification === "10th Pass"} onChange={handleInputChange} required />10th Pass</label>
                    <label><input type="radio" name="highest_qualification" value="12th Pass (Science)"
                      checked={form.highest_qualification === "12th Pass (Science)"} onChange={handleInputChange} />12th Pass (Science)</label>
                    <label><input type="radio" name="highest_qualification" value="12th Pass (Arts)"
                      checked={form.highest_qualification === "12th Pass (Arts)"} onChange={handleInputChange} />12th Pass (Arts)</label>
                    <label><input type="radio" name="highest_qualification" value="12th Pass (Commerce)"
                      checked={form.highest_qualification === "12th Pass (Commerce)"} onChange={handleInputChange} />12th Pass (Commerce)</label>
                  </div>
                  {errors.highest_qualification && <span className="text-red-500 text-sm">{errors.highest_qualification}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Board/University</label>
                  <Input name="board_university" value={form.board_university} onChange={handleInputChange} />
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Year of Passing</label>
                  <Input type="number" min={1950} max={new Date().getFullYear()} name="year_of_passing" value={form.year_of_passing} onChange={handleInputChange} />
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">Percentage/CGPA</label>
                  <Input type="number" step="0.01" min={0} max={100} name="percentage_cgpa" value={form.percentage_cgpa} onChange={handleInputChange} />
                </div>
                <div><label className="block text-sm font-semibold text-foreground/80 mb-1.5">School/College Name</label>
                  <Input name="school_college_name" value={form.school_college_name} onChange={handleInputChange} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Preferred Course</label>
                  <select name="preferred_course" value={form.preferred_course} onChange={handleInputChange} required className={`field-base ${errors.preferred_course ? "field-error" : ""}`}>
                    <option value="">Select Course</option>
                    {courseOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  {errors.preferred_course && <span className="text-red-500 text-sm">{errors.preferred_course}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Batch Time Preference</label>
                  <div className="flex flex-wrap gap-4">
                    <label><input type="radio" name="batch_time_preference" value="Morning"
                      checked={form.batch_time_preference === "Morning"} onChange={handleInputChange} required />Morning</label>
                    <label><input type="radio" name="batch_time_preference" value="Afternoon"
                      checked={form.batch_time_preference === "Afternoon"} onChange={handleInputChange} />Afternoon</label>
                    <label><input type="radio" name="batch_time_preference" value="Evening"
                      checked={form.batch_time_preference === "Evening"} onChange={handleInputChange} />Evening</label>
                  </div>
                  {errors.batch_time_preference && <span className="text-red-500 text-sm">{errors.batch_time_preference}</span>}
                </div>
              </div>
            </div>

            {/* Document Checklist */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text flex items-center gap-2">
                <FileText size={28} className="text-brand-amber" />
                Document Uploads
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                {/* Each file: customize with SVG if you want, matching the application form style */}
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Passport Size Photograph
                  </label>
                  <div className="relative">
                    <Input type="file" name="passport_size_photo" accept="image/*" onChange={handleFileChange} className="hidden" id="passport_size_photo" />
                    <label htmlFor="passport_size_photo" className={`dropzone ${errors.passport_size_photo ? "dropzone-error" : ""}`}>
                      <div className="text-center">
                        <span className="block text-sm text-muted-foreground">{form.passport_size_photo ? form.passport_size_photo.name : "Click to upload"}</span>
                      </div>
                    </label>
                  </div>
                  {errors.passport_size_photo && <span className="text-red-500 text-sm">{errors.passport_size_photo}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">10th/12th Marksheet</label>
                  <div className="relative">
                    <Input type="file" name="marksheet_10_12" accept="application/pdf,image/*" onChange={handleFileChange} className="hidden" id="marksheet_10_12" />
                    <label htmlFor="marksheet_10_12" className={`dropzone ${errors.marksheet_10_12 ? "dropzone-error" : ""}`}>
                      <div className="text-center">
                        <span className="block text-sm text-muted-foreground">{form.marksheet_10_12 ? form.marksheet_10_12.name : "Click to upload"}</span>
                      </div>
                    </label>
                  </div>
                  {errors.marksheet_10_12 && <span className="text-red-500 text-sm">{errors.marksheet_10_12}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Aadhar Card (Address Proof)</label>
                  <div className="relative">
                    <Input type="file" name="aadhar_card_copy" accept="application/pdf,image/*" onChange={handleFileChange} className="hidden" id="aadhar_card_copy" />
                    <label htmlFor="aadhar_card_copy" className={`dropzone ${errors.aadhar_card_copy ? "dropzone-error" : ""}`}>
                      <div className="text-center">
                        <span className="block text-sm text-muted-foreground">{form.aadhar_card_copy ? form.aadhar_card_copy.name : "Click to upload"}</span>
                      </div>
                    </label>
                  </div>
                  {errors.aadhar_card_copy && <span className="text-red-500 text-sm">{errors.aadhar_card_copy}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Ration Card</label>
                  <div className="relative">
                    <Input type="file" name="signature" accept="image/*" onChange={handleFileChange} className="hidden" id="signature" />
                    <label htmlFor="signature" className={`dropzone ${errors.signature ? "dropzone-error" : ""}`}>
                      <div className="text-center">
                        <span className="block text-sm text-muted-foreground">{form.signature ? form.signature.name : "Click to upload"}</span>
                      </div>
                    </label>
                  </div>
                  {errors.signature && <span className="text-red-500 text-sm">{errors.signature}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Caste Certificate (If applicable)</label>
                  <div className="relative">
                    <Input type="file" name="caste_certificate" accept="application/pdf,image/*" onChange={handleFileChange} className="hidden" id="caste_certificate" />
                    <label htmlFor="caste_certificate" className={`dropzone ${errors.caste_certificate ? "dropzone-error" : ""}`}>
                      <div className="text-center">
                        <span className="block text-sm text-muted-foreground">{form.caste_certificate ? form.caste_certificate.name : "Click to upload"}</span>
                      </div>
                    </label>
                  </div>
                  {errors.caste_certificate && <span className="text-red-500 text-sm">{errors.caste_certificate}</span>}
                </div>
              </div>
            </div>

            {/* Declaration */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text flex items-center gap-2">
                <CheckCircle2 size={28} className="text-emerald-600" />
                Declaration
              </h3>
              <div className="flex flex-col md:grid md:grid-cols-3 gap-x-6 gap-y-6">
                <label className="md:col-span-2 flex items-center gap-3 text-gray-700">
                  <input
                    type="checkbox"
                    name="declaration"
                    checked={form.declaration}
                    onChange={handleInputChange}
                    required
                  />
                  I hereby declare that the information provided above is true to the best of my knowledge.
                  {errors.declaration && <span className="text-red-500 text-sm ml-3">{errors.declaration}</span>}
                </label>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Date</label>
                  <Input type="date" name="declaration_date" value={form.declaration_date} onChange={handleInputChange} required className={errors.declaration_date ? "border-red-500" : ""} />
                  {errors.declaration_date && <span className="text-red-500 text-sm">{errors.declaration_date}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Place</label>
                  <Input name="declaration_place" value={form.declaration_place} onChange={handleInputChange} required className={errors.declaration_place ? "border-red-500" : ""} />
                  {errors.declaration_place && <span className="text-red-500 text-sm">{errors.declaration_place}</span>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">Signed Applicant Acknowledgment</label><span className="text-red-500">*</span>
                  <a
                    href="https://mnmsfgytqybdjlbpelim.supabase.co/storage/v1/object/sign/documents/officialDocuments/Meenamma_Security_Confirmation_Form.pdf?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80MjliYWE3My0yMWE4LTRkNjYtOTljMC05NzY0NWU1MzJhYzkiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJkb2N1bWVudHMvb2ZmaWNpYWxEb2N1bWVudHMvTWVlbmFtbWFfU2VjdXJpdHlfQ29uZmlybWF0aW9uX0Zvcm0ucGRmIiwiaWF0IjoxNzcxNjUxNDA0LCJleHAiOjI0MDIzNzE0MDR9.qAVD4dBEYoBrMhi7tJhkxzotH4lW3v9oPwhRTiWRD0Q"
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="text-primary underline text-sm"
                  >
                    Download Meenamma Security Confirmation Form
                  </a>
                  <Input type="file" name="applicant_signature" accept="image/*" onChange={handleFileChange} className="hidden" id="applicant_signature" />
                  <label htmlFor="applicant_signature" className={`dropzone ${errors.applicant_signature ? "dropzone-error" : ""}`}>
                    <div className="text-center">
                      <span className="block text-sm text-muted-foreground">{form.applicant_signature ? form.applicant_signature.name : "Click to upload"}</span>
                    </div>
                  </label>
                  {errors.applicant_signature && <span className="text-red-500 text-sm">{errors.applicant_signature}</span>}
                </div>
              </div>
            </div>

            {/* Submit */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex justify-center pt-4"
            >
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transform transition hover:scale-105 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  "Register"
                )}
              </button>
            </motion.div>
            {submitSuccess && (
              <div className="text-green-600 font-semibold mt-4 text-center">
                <CheckCircle className="inline-block mr-2" />Form submitted successfully!
              </div>
            )}
          </form>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-center text-muted-foreground"
        >
          <p className="flex items-center justify-center gap-2">
            <CheckCircle className="text-green-500" size={20} />
            Your information is secure and will be used solely for admission
            processing.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AdmissionsSection;